import { AlertCircle } from 'lucide-react';
import {
  cloneElement,
  forwardRef,
  type InputHTMLAttributes,
  isValidElement,
  type ReactElement,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
  useId,
} from 'react';
import { isFieldKey } from '../../features/knowledge/fieldTerms';
import { TermHint } from '../../features/knowledge/TermHint';
import { cn } from '../../lib/cn';

type FieldProps = {
  label: ReactNode;
  /** Пояснення під полем (правило фірми, формат). */
  hint?: ReactNode;
  /** Повідомлення сервера або перевірки — показується дослівно. */
  error?: string | null;
  required?: boolean;
  /** Позначка «обов’язково за правилами фірми». */
  requiredNote?: string;
  className?: string;
  children: ReactElement<{ id?: string; 'aria-describedby'?: string; 'aria-invalid'?: boolean; 'aria-required'?: boolean }>;
  labelHidden?: boolean;
  aside?: ReactNode;
  /**
   * Значок «?» з поясненням поняття поряд із підписом: ключ поля (див. features/knowledge/fieldTerms.ts,
   * напр. 'content.material.commercialValue') або сам термін ('КЦ'). Якщо поняття немає в довіднику — значка немає.
   */
  term?: string;
};

/** Поле форми: підпис над полем, пояснення й помилка під ним, зв'язок через aria-describedby. */
export function Field({ label, hint, error, required, requiredNote, className, children, labelHidden, aside, term }: FieldProps) {
  const id = useId();
  const hintId = hint ? `${id}-hint` : undefined;
  const errId = error ? `${id}-err` : undefined;
  const describedBy = [errId, hintId].filter(Boolean).join(' ') || undefined;
  const control = isValidElement(children)
    ? cloneElement(children, {
        id: children.props.id ?? id,
        'aria-describedby': describedBy,
        'aria-invalid': error ? true : undefined,
        // Обов'язковість — для допоміжних технологій; перевіряємо самі (українською), без спливних підказок браузера.
        'aria-required': required || undefined,
      })
    : children;
  const labelEl = (
    <label htmlFor={children.props.id ?? id} className="text-meta font-medium text-fg">
      {label}
      {required && (
        // Обов’язковість уже оголошує aria-required у полі; типова помітка — лише для очей,
        // власне пояснення («за правилами фірми») лишається в назві поля.
        <span aria-hidden={requiredNote ? undefined : true} className="ml-1.5 text-caption font-normal text-fg-3">
          {requiredNote ?? 'обов’язково'}
        </span>
      )}
    </label>
  );
  const termKey = term?.trim();
  return (
    <div className={cn('flex min-w-0 flex-col gap-1.5', className)} data-field-error={error ? '' : undefined}>
      <div className={cn('flex items-baseline justify-between gap-2', labelHidden && 'sr-only')}>
        {termKey && !labelHidden ? (
          // Значок поза <label>: інакше його текст увійшов би в назву поля для зчитувачів.
          <span className="flex min-w-0 items-center gap-1">
            {labelEl}
            <TermHint
              fieldKey={isFieldKey(termKey) ? termKey : undefined}
              term={isFieldKey(termKey) ? undefined : termKey}
              label={typeof label === 'string' ? label : undefined}
            />
          </span>
        ) : (
          labelEl
        )}
        {aside}
      </div>
      {control}
      {error && (
        <p id={errId} className="flex items-start gap-1.5 text-meta text-danger-fg" role="alert">
          <AlertCircle aria-hidden className="mt-0.5 size-3.5 shrink-0" strokeWidth={2} />
          <span>{error}</span>
        </p>
      )}
      {hint && (
        <p id={hintId} className="text-meta text-fg-3">
          {hint}
        </p>
      )}
    </div>
  );
}

const controlBase =
  'w-full rounded-sm border bg-surface text-body text-fg transition-[border-color,box-shadow] duration-150 ' +
  'placeholder:text-fg-3 hover:border-fg-3 focus:border-focus focus:outline-none focus:ring-2 focus:ring-focus/20 ' +
  'disabled:cursor-not-allowed disabled:bg-surface-2 disabled:text-fg-3 ' +
  'aria-[invalid=true]:border-danger aria-[invalid=true]:focus:ring-danger/25';

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement> & { invalid?: boolean }>(function Input(
  { className, invalid, ...rest },
  ref,
) {
  return (
    <input
      ref={ref}
      aria-invalid={invalid || rest['aria-invalid']}
      className={cn(controlBase, 'h-[var(--control-md)] border-line-input px-2.5', className)}
      {...rest}
    />
  );
});

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(function Textarea(
  { className, rows = 3, ...rest },
  ref,
) {
  return (
    <textarea
      ref={ref}
      rows={rows}
      className={cn(controlBase, 'min-h-[72px] resize-y border-line-input px-2.5 py-2 leading-relaxed', className)}
      {...rest}
    />
  );
});

export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement> & { wrapperClassName?: string }>(function Select(
  { className, wrapperClassName, children, ...rest },
  ref,
) {
  return (
    <span className={cn('relative flex', wrapperClassName)}>
      <select
        ref={ref}
        className={cn(controlBase, 'h-[var(--control-md)] appearance-none border-line-input pr-8 pl-2.5', className)}
        {...rest}
      >
        {children}
      </select>
      <svg aria-hidden viewBox="0 0 16 16" className="pointer-events-none absolute top-1/2 right-2.5 size-3.5 -translate-y-1/2 text-fg-3">
        <path d="M4 6l4 4 4-4" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </span>
  );
});

export function Checkbox({
  checked,
  onChange,
  label,
  description,
  disabled,
  id,
  className,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: ReactNode;
  description?: ReactNode;
  disabled?: boolean;
  id?: string;
  className?: string;
}) {
  const auto = useId();
  const cid = id ?? auto;
  return (
    <div className={cn('flex items-start gap-2.5', className)}>
      <input
        id={cid}
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        aria-describedby={description ? `${cid}-d` : undefined}
        className="peer mt-0.5 size-4 shrink-0 cursor-pointer appearance-none rounded-xs border border-line-input bg-surface transition-colors checked:border-accent checked:bg-accent disabled:cursor-not-allowed disabled:opacity-50"
        style={{
          backgroundImage: checked
            ? "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Cpath d='M4 8.5l2.5 2.5L12 5.5' fill='none' stroke='white' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E\")"
            : undefined,
          backgroundSize: '100%',
        }}
      />
      <label htmlFor={cid} className="min-w-0 cursor-pointer text-body text-fg peer-disabled:cursor-not-allowed peer-disabled:text-fg-3">
        {label}
        {description && (
          <span id={`${cid}-d`} className="mt-0.5 block text-meta text-fg-3">
            {description}
          </span>
        )}
      </label>
    </div>
  );
}

export function Switch({
  checked,
  onChange,
  label,
  description,
  disabled,
  className,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: ReactNode;
  description?: ReactNode;
  disabled?: boolean;
  className?: string;
}) {
  const id = useId();
  return (
    <div className={cn('flex items-start justify-between gap-4', className)}>
      <div className="min-w-0">
        <label htmlFor={id} className="cursor-pointer text-body text-fg">
          {label}
        </label>
        {description && (
          <p id={`${id}-d`} className="mt-0.5 text-meta text-fg-3">
            {description}
          </p>
        )}
      </div>
      <button
        id={id}
        type="button"
        role="switch"
        aria-checked={checked}
        aria-describedby={description ? `${id}-d` : undefined}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={cn(
          'relative mt-0.5 inline-flex h-5 w-9 shrink-0 items-center rounded-full border transition-colors duration-150',
          checked ? 'border-accent bg-accent' : 'border-line-input bg-surface-3',
          'disabled:cursor-not-allowed disabled:opacity-50',
        )}
      >
        <span
          aria-hidden
          className={cn(
            'ers-turn inline-block size-3.5 rounded-full bg-surface shadow-[0_1px_2px_rgb(0_0_0/0.2)]',
            checked ? 'translate-x-[18px]' : 'translate-x-[2px]',
          )}
        />
      </button>
    </div>
  );
}

export function RadioGroup<T extends string>({
  value,
  onChange,
  options,
  name,
  label,
  className,
}: {
  value: T;
  onChange: (v: T) => void;
  options: { value: T; label: ReactNode; description?: ReactNode; disabled?: boolean }[];
  name: string;
  label: string;
  className?: string;
}) {
  return (
    <fieldset className={cn('flex flex-col gap-2', className)}>
      <legend className="mb-1.5 text-meta font-medium text-fg">{label}</legend>
      {options.map((o) => (
        <label
          key={o.value}
          className={cn(
            'flex cursor-pointer items-start gap-2.5 rounded-sm border px-3 py-2 transition-colors',
            value === o.value ? 'border-accent bg-accent-soft' : 'border-line hover:bg-surface-2',
            o.disabled && 'cursor-not-allowed opacity-50',
          )}
        >
          <input
            type="radio"
            name={name}
            value={o.value}
            checked={value === o.value}
            disabled={o.disabled}
            onChange={() => onChange(o.value)}
            className="mt-1 size-3.5 shrink-0 accent-[var(--c-accent)]"
          />
          <span className="min-w-0">
            <span className="block text-body text-fg">{o.label}</span>
            {o.description && <span className="block text-meta text-fg-3">{o.description}</span>}
          </span>
        </label>
      ))}
    </fieldset>
  );
}

/** Помилка форми: detail сервера дослівно + перелік полів, яких немає у формі. */
export function FormAlert({ title, detail, items, tone = 'danger', children }: { title?: string; detail?: ReactNode; items?: string[]; tone?: 'danger' | 'warning' | 'info'; children?: ReactNode }) {
  return (
    <div
      role={tone === 'danger' ? 'alert' : 'status'}
      className={cn(
        'rounded-sm border px-3 py-2.5 text-meta',
        tone === 'danger' && 'border-danger-line bg-danger-soft text-danger-fg',
        tone === 'warning' && 'border-warning-line bg-warning-soft text-warning-fg',
        tone === 'info' && 'border-info-line bg-info-soft text-info-fg',
      )}
    >
      {title && <p className="font-semibold">{title}</p>}
      {detail && <p className={cn(title && 'mt-0.5')}>{detail}</p>}
      {items && items.length > 0 && (
        <ul className="mt-1 list-disc pl-4">
          {items.map((i) => (
            <li key={i}>{i}</li>
          ))}
        </ul>
      )}
      {children}
    </div>
  );
}
